import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Box, Button, Group, Text, TextInput } from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useSalon } from "../../../context/SalonContext";
import { useProducts } from "../../../context/ProductsContext";
import { useClientGroups } from "../../../hooks/useClientGroups";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import {
  ClientGroup,
  ClientGroupMemberClient,
} from "../../../types/clientGroups";
import GroupTypeCell from "./GroupTypeCell";
import { RuleLabels, composeClauses } from "./ruleModel";
import sx from "../../sales/shared/sales.module.css";

interface Props {
  group: ClientGroup;
  overlay: boolean;
  onClose: () => void;
  onEditRules: () => void;
  onManageMembers: () => void;
  onDelete: () => void;
  /** bump to force a member refetch after a membership edit */
  refreshKey?: number;
}

// Members section: load the whole membership (backend returns all when no
// limit is passed) and search/scroll on the client. `total` is the group size;
// the visible list can be narrowed by the search box.

// Footer buttons: equal width in one row, height grows to fit a label that
// wraps to two lines, and a shared min-height so all three match (per feedback).
const FOOTER_BTN = {
  root: {
    flex: 1,
    height: "auto",
    minHeight: 44,
    paddingTop: 6,
    paddingBottom: 6,
  },
  label: {
    whiteSpace: "normal" as const,
    lineHeight: 1.15,
    textAlign: "center" as const,
  },
};

export default function ClientGroupPanel({
  group,
  overlay,
  onClose,
  onEditRules,
  onManageMembers,
  onDelete,
  refreshKey,
}: Props) {
  const { services, employees } = useSalon() as any;
  const { products } = useProducts() as any;
  const { getMembers } = useClientGroups();

  const [members, setMembers] = useState<ClientGroupMemberClient[]>([]);
  const [total, setTotal] = useState<number>(group.member_count ?? 0);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [memberSearch, setMemberSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoadingMembers(true);
    setMemberSearch("");
    getMembers(group.id) // no limit -> all members
      .then((res) => {
        if (cancelled) return;
        setMembers(res.clients);
        setTotal(res.total);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMembers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [group.id, getMembers, refreshKey]);

  const labels: RuleLabels = useMemo(
    () => ({
      services: Object.fromEntries(
        (services || []).map((s: any) => [
          String(s.uuid ?? s.id ?? s.salon_service_id),
          s.name,
        ]),
      ),
      products: Object.fromEntries(
        (products || []).map((p: any) => [String(p.id), p.name]),
      ),
      staff: Object.fromEntries(
        (employees || []).map((e: any) => [
          String(e.id ?? e.staff_user_id),
          e.name || `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim(),
        ]),
      ),
    }),
    [services, products, employees],
  );

  const isStatic = group.group_type === "static";
  const clauses = isStatic
    ? [
        {
          connector: null as string | null,
          text: `Membership is managed by hand — ${total} client${
            total === 1 ? "" : "s"
          } added.`,
        },
      ]
    : composeClauses(group.rules, labels);

  const updated = new Date(group.updated_dt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const name = (c: { first_name: string; last_name: string }) =>
    `${c.first_name} ${c.last_name}`.trim() || "—";

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const n = `${m.first_name ?? ""} ${m.last_name ?? ""}`.toLowerCase();
      return n.includes(q) || (m.email ?? "").toLowerCase().includes(q);
    });
  }, [members, memberSearch]);

  const actionLabel = isStatic ? "Manage Members" : "Edit Rules";
  const onAction = isStatic ? onManageMembers : onEditRules;

  return (
    <Box
      className={`${sx.detailPanel} ${overlay ? sx.detailPanelOverlay : ""}`}
    >
      {/* Header */}
      <Box className={sx.panelHeader}>
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Text className={sx.panelTitle} lineClamp={1}>
            {group.name}
          </Text>
          <Text className={sx.panelMeta}>
            {total} member{total === 1 ? "" : "s"} · updated {updated}
          </Text>
        </Box>
        <Group gap="sm" wrap="nowrap" align="center">
          <GroupTypeCell type={group.group_type} />
          <IconX
            size={16}
            stroke={2.4}
            style={{ cursor: "pointer", color: "#6a6a6a" }}
            onClick={onClose}
          />
        </Group>
      </Box>

      {/* Body */}
      <Box className={sx.panelBody}>
        <Box>
          <Group justify="space-between" align="baseline" mb={0}>
            <Box className={sx.sectionLabel} mb={0}>
              Definition
            </Box>
            <Text
              size="12.5px"
              fw={500}
              c="#1a7285"
              style={{ cursor: "pointer" }}
              onClick={onAction}
            >
              {actionLabel}
            </Text>
          </Group>
          <Box
            mt={10}
            style={{ border: "1px solid #e6e6e6", borderRadius: 12 }}
          >
            {clauses.map((cl, i) => (
              <Box
                key={i}
                p="12px 14px"
                style={{ borderTop: i > 0 ? "1px solid #ededed" : undefined }}
              >
                {cl.connector && (
                  <Text
                    size="10px"
                    fw={700}
                    tt="uppercase"
                    c="#8a8a8a"
                    mb={5}
                    style={{ letterSpacing: ".06em" }}
                  >
                    {cl.connector}
                  </Text>
                )}
                <Text size="13.5px" c="#1a1a1a" style={{ lineHeight: 1.5 }}>
                  {cl.text}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>

        <Box mt={28}>
          <Group justify="space-between" align="baseline">
            <Box className={sx.sectionLabel} mb={0}>
              Members
            </Box>
            {total > 0 && (
              <Text className={sx.resultCount}>
                {memberSearch.trim()
                  ? `${filteredMembers.length} of ${total} match`
                  : `Showing ${members.length} of ${total}`}
              </Text>
            )}
          </Group>
          {total > 5 && (
            <TextInput
              size="xs"
              mt={8}
              maw={320}
              placeholder="Search members"
              leftSection={<IconSearch size={14} />}
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.currentTarget.value)}
            />
          )}
          <Box mt={8} style={{ maxHeight: 360, overflowY: "auto" }}>
            {filteredMembers.map((m) => (
              <Group
                key={m.id}
                wrap="nowrap"
                gap={11}
                py={7}
                style={{ borderBottom: "1px solid #ededed" }}
              >
                <Avatar size="sm" radius="xl" color="var(--brand-teal)">
                  {(m.first_name?.[0] ?? "?").toUpperCase()}
                </Avatar>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="13.5px" fw={500} c="#1a1a1a" lineClamp={1}>
                    {name(m)}
                  </Text>
                  <Text size="12px" c="#8a8a8a" lineClamp={1}>
                    {m.email}
                  </Text>
                </Box>
                <Text
                  size="12px"
                  c="#8a8a8a"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {formatCurrency(Number(m.lifetime_spend ?? 0))}
                </Text>
              </Group>
            ))}
            {loadingMembers && members.length === 0 && (
              <Text size="13px" c="#8a8a8a" py="sm">
                Loading members…
              </Text>
            )}
            {!loadingMembers && members.length === 0 && (
              <Text size="13px" c="#8a8a8a" py="sm">
                No members.
              </Text>
            )}
            {members.length > 0 && filteredMembers.length === 0 && (
              <Text size="13px" c="#8a8a8a" py="sm">
                No members match “{memberSearch.trim()}”.
              </Text>
            )}
          </Box>
        </Box>
      </Box>

      {/* Footer — three equal buttons in one row; each grows tall enough for a
          two-line label, and all share a min-height so they stay consistent. */}
      <Box className={sx.panelFooter}>
        <Button variant="default" size="sm" styles={FOOTER_BTN} disabled>
          Send Campaign
        </Button>
        <Button
          variant="default"
          size="sm"
          styles={FOOTER_BTN}
          onClick={onAction}
        >
          {actionLabel}
        </Button>
        <Button
          variant="light"
          size="sm"
          color="red"
          styles={FOOTER_BTN}
          onClick={onDelete}
        >
          Delete
        </Button>
      </Box>
    </Box>
  );
}
